import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@shared/services/api';
import { Card, Button, Input } from '@shared/components/ui';
import { Trash2, CheckCircle2, ArrowLeft, AlertTriangle } from 'lucide-react';

type PageStatus =
  'request' | 'requesting' | 'emailSent' | 'confirm' | 'confirming' | 'success' | 'error';

export default function DeleteAccount() {
  const { t } = useTranslation('settings');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<PageStatus>(token ? 'confirm' : 'request');
  const [password, setPassword] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Visiting the email link only displays an explicit confirmation screen.
    // The destructive API request runs after the user presses the button.
    setStatus(token ? 'confirm' : 'request');
    setAcknowledged(false);
    setMessage('');
  }, [token]);

  const handleRequest = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === 'requesting') return;
    setStatus('requesting');
    setMessage('');
    try {
      await api.post('/auth/delete-account/request', { password });
      setPassword('');
      setStatus('emailSent');
    } catch {
      setStatus('request');
      setMessage(t('deleteAccount.errors.requestFailed'));
    }
  };

  const handleConfirm = async () => {
    if (!token || !acknowledged) return;
    setStatus('confirming');
    setMessage('');
    try {
      await api.get(`/auth/delete-account/confirm?token=${encodeURIComponent(token)}`);
      setMessage(t('deleteAccount.success.defaultMessage'));
      setStatus('success');
    } catch {
      setMessage(t('deleteAccount.errors.failed'));
      setStatus('error');
    }
  };

  const isBusy = status === 'requesting' || status === 'confirming';

  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-light p-4 dark:bg-surface-dark">
      <div className="w-full max-w-lg animate-in fade-in zoom-in duration-500">
        <header className="mb-6 text-center sm:mb-8">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-state-danger-light shadow-soft dark:bg-state-danger-dark sm:h-16 sm:w-16">
            <Trash2 aria-hidden="true" className="h-7 w-7 text-white" />
          </div>
          <h1 className="mt-4 text-2xl font-bold tracking-tight text-text-primary-light dark:text-text-primary-dark sm:mt-5 sm:text-3xl">
            {t('deleteAccount.title')}{' '}
            <span className="text-state-danger-light dark:text-state-danger-dark">
              {t('deleteAccount.titleHighlight')}
            </span>
          </h1>
        </header>

        <Card className="space-y-5 p-5 shadow-elevated sm:space-y-6 sm:p-8">
          {(status === 'request' || status === 'requesting') && (
            <>
              <div>
                <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark sm:text-xl">
                  {t('deleteAccount.request.title')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark">
                  {t('deleteAccount.request.description')}
                </p>
              </div>
              {message && (
                <p
                  role="alert"
                  className="rounded-lg border border-state-danger-light/20 bg-state-danger-bg-light p-3 text-sm text-state-danger-light dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark dark:text-state-danger-dark"
                >
                  {message}
                </p>
              )}
              <form onSubmit={handleRequest} className="space-y-5">
                <fieldset disabled={isBusy} className="space-y-5">
                  <Input
                    id="delete-account-password"
                    label={t('deleteAccount.request.password')}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                  />
                  <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      className="min-h-11"
                      onClick={() => navigate('/profile')}
                    >
                      {t('deleteAccount.request.cancel')}
                    </Button>
                    <Button
                      type="submit"
                      isLoading={status === 'requesting'}
                      className="min-h-11 bg-[#b42318] text-white hover:bg-[#912018] dark:bg-[#b42318] dark:text-white dark:hover:bg-[#912018]"
                    >
                      {t('deleteAccount.request.submit')}
                    </Button>
                  </div>
                </fieldset>
              </form>
            </>
          )}

          {status === 'emailSent' && (
            <section role="status" aria-live="polite" className="space-y-5 text-center">
              <CheckCircle2
                aria-hidden="true"
                className="mx-auto h-12 w-12 text-state-ok-light dark:text-state-ok-dark"
              />
              <div>
                <h2 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('deleteAccount.request.emailSentTitle')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark">
                  {t('deleteAccount.request.emailSentDescription')}
                </p>
              </div>
              <Button
                variant="secondary"
                className="min-h-11 w-full"
                onClick={() => navigate('/profile')}
              >
                {t('deleteAccount.request.returnProfile')}
              </Button>
            </section>
          )}

          {status === 'confirm' && (
            <section className="space-y-5">
              <div className="flex items-start gap-3 rounded-lg border border-state-danger-light/20 bg-state-danger-bg-light p-4 dark:border-state-danger-dark/20 dark:bg-state-danger-bg-dark">
                <AlertTriangle
                  aria-hidden="true"
                  className="mt-0.5 h-5 w-5 shrink-0 text-state-danger-light dark:text-state-danger-dark"
                />
                <div>
                  <h2 className="font-semibold text-state-danger-light dark:text-state-danger-dark">
                    {t('deleteAccount.confirm.title')}
                  </h2>
                  <p className="mt-1 text-sm leading-5 text-text-primary-light dark:text-text-primary-dark">
                    {t('deleteAccount.confirm.description')}
                  </p>
                </div>
              </div>
              <label
                htmlFor="delete-account-acknowledgement"
                className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border border-surface-border-light p-3 text-sm text-text-primary-light dark:border-surface-border-dark dark:text-text-primary-dark"
              >
                <input
                  id="delete-account-acknowledgement"
                  type="checkbox"
                  checked={acknowledged}
                  onChange={(event) => setAcknowledged(event.target.checked)}
                  className="mt-0.5 h-5 w-5 shrink-0 accent-state-danger-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-state-danger-light"
                />
                <span>{t('deleteAccount.confirm.acknowledgement')}</span>
              </label>
              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-11"
                  onClick={() => navigate('/profile')}
                >
                  {t('deleteAccount.confirm.cancel')}
                </Button>
                <Button
                  type="button"
                  disabled={!acknowledged}
                  className="min-h-11 bg-[#b42318] text-white hover:bg-[#912018] disabled:opacity-50 dark:bg-[#b42318] dark:text-white dark:hover:bg-[#912018]"
                  onClick={handleConfirm}
                >
                  {t('deleteAccount.confirm.submit')}
                </Button>
              </div>
            </section>
          )}

          {(status === 'confirming' || status === 'requesting') && (
            <p
              role="status"
              aria-live="polite"
              className="text-center text-sm text-text-muted-light dark:text-text-muted-dark"
            >
              {t('deleteAccount.loading.description')}
            </p>
          )}

          {status === 'success' && (
            <section role="status" aria-live="polite" className="space-y-5 text-center">
              <CheckCircle2
                aria-hidden="true"
                className="mx-auto h-12 w-12 text-state-ok-light dark:text-state-ok-dark"
              />
              <div>
                <h2 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('deleteAccount.success.title')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark">
                  {message}
                </p>
              </div>
              <Button
                className="min-h-11 w-full"
                onClick={() => {
                  localStorage.removeItem('auth_token');
                  navigate('/login', { replace: true });
                }}
              >
                {t('deleteAccount.success.returnLogin')}
              </Button>
            </section>
          )}

          {status === 'error' && (
            <section role="alert" className="space-y-5 text-center">
              <AlertTriangle
                aria-hidden="true"
                className="mx-auto h-12 w-12 text-state-warn-light dark:text-state-warn-dark"
              />
              <div>
                <h2 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {t('deleteAccount.error.title')}
                </h2>
                <p className="mt-2 text-sm leading-6 text-text-muted-light dark:text-text-muted-dark">
                  {message}
                </p>
              </div>
              {token ? (
                <Button
                  variant="outline"
                  className="min-h-11 w-full"
                  onClick={() => {
                    setStatus('confirm');
                    setAcknowledged(false);
                    setMessage('');
                  }}
                >
                  {t('deleteAccount.error.tryAgain')}
                </Button>
              ) : (
                <Button variant="outline" className="min-h-11 w-full" onClick={() => navigate('/')}>
                  <ArrowLeft aria-hidden="true" className="mr-2 h-4 w-4" />{' '}
                  {t('deleteAccount.error.returnHome')}
                </Button>
              )}
            </section>
          )}
        </Card>
      </div>
      {isBusy && (
        <span className="sr-only" aria-live="polite">
          {t('deleteAccount.loading.title')}
        </span>
      )}
    </main>
  );
}
